import { startCase } from 'lodash-es';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { TractNRIFormulaData } from 'components/tract-nri-card';
import type { NriMatrixFormulaInputs } from 'types/api-responses';

export type AllocationMethod = 'acreage' | 'lateralLength';
export type AllocationSource = 'plat' | 'gis';
export type NriCalculationMethod = 'nra' | 'nma';

// Template literal types for type-safe property access
type Method = 'acreage' | 'lateral';
type UatKey = `uat${'Gis' | ''}${'Ac' | 'LateralLength'}`;
type UnitKey = `unit${'Gis' | ''}Ac` | `unitLateral${'Gis' | ''}Length`;

const uatKeyMap: Record<Method, Record<AllocationSource, UatKey>> = {
  acreage: { plat: 'uatAc', gis: 'uatGisAc' },
  lateral: { plat: 'uatLateralLength', gis: 'uatGisLateralLength' },
};

const unitKeyMap: Record<Method, Record<AllocationSource, UnitKey>> = {
  acreage: { plat: 'unitAc', gis: 'unitGisAc' },
  lateral: { plat: 'unitLateralLength', gis: 'unitLateralGisLength' },
};

export interface UatValueConfig {
  uatValue: number;
  unitValue: number;
  /**
   * The popup's third-term denominator (participation / this). Under legal
   * basis it is the participation denominator, not the UAT's GIS acreage —
   * conflating the two put plat acreage on the matrix's GIS row (#7418).
   */
  participationDenominatorAc: number;
}

/**
 * Extracts UAT values from formula inputs based on allocation method and source.
 * Uses template literal types for type-safe property access.
 * @param {NriMatrixFormulaInputs | null} formulaInputs - The formula inputs containing UAT and unit values
 * @param {number | null} rowValue - Fallback value if formula inputs are missing
 * @param {AllocationMethod} allocationMethod - The allocation method (acreage or lateralLength)
 * @param {AllocationSource} source - The allocation source (plat or gis)
 * @param {boolean} hasAllocationManager - Whether an allocation manager exists
 * @return {UatValueConfig} Configuration object with uatValue, unitValue, and uatGisAc
 */
export const getUatValues = (
  formulaInputs: NriMatrixFormulaInputs | null,
  rowValue: number | null,
  allocationMethod: AllocationMethod,
  source: AllocationSource,
  hasAllocationManager: boolean
): UatValueConfig => {
  if (!hasAllocationManager) {
    const unitGisAc = formulaInputs?.unitGisAc ?? 0;
    return {
      uatValue: unitGisAc,
      unitValue: unitGisAc,
      participationDenominatorAc: unitGisAc,
    };
  }

  const method: Method =
    allocationMethod === 'lateralLength' ? 'lateral' : 'acreage';
  const uatKey = uatKeyMap[method][source];
  const unitKey = unitKeyMap[method][source];

  return {
    uatValue: formulaInputs?.[uatKey] ?? rowValue ?? 0,
    unitValue: formulaInputs?.[unitKey] ?? 0,
    // uatGisAc fallback: payloads predating the key split carry only the
    // overloaded field, where GIS acreage was the pre-split meaning.
    participationDenominatorAc:
      formulaInputs?.participationDenominatorAc ?? formulaInputs?.uatGisAc ?? 0,
  };
};

/**
 * Builds TractNRIFormulaData from formula inputs.
 * @param {NriMatrixFormulaInputs | null} formulaInputs - The formula inputs to transform
 * @return {TractNRIFormulaData | null} TractNRIFormulaData object or null if inputs are null
 */
export const buildTractNriFormula = (
  formulaInputs: NriMatrixFormulaInputs | null
): TractNRIFormulaData | null => {
  if (!formulaInputs) return null;

  return {
    royaltyRate: formulaInputs.leaseRoyaltyRate ?? 0,
    netMineralAcres: formulaInputs.nma ?? 0,
    totalTractAcres: formulaInputs.grossAcres ?? 0,
    netRoyaltyAcres: formulaInputs.nra ?? undefined,
    instrumentType: formulaInputs.instrumentType,
    orriBurdens: formulaInputs.orriBurdens ?? 0,
  };
};

/**
 * Normalizes netAcreageUnits to lowercase 'nma' | 'nra'.
 * Defaults to 'nra' if null/undefined.
 * @param {string | null | undefined} netAcreageUnits - The net acreage units string to normalize
 * @return {NriCalculationMethod} Normalized NriCalculationMethod ('nma' or 'nra')
 */
export const toNriCalculationMethod = (
  netAcreageUnits: string | null | undefined
): NriCalculationMethod =>
  (netAcreageUnits?.toLowerCase() as NriCalculationMethod) || 'nra';

/**
 * Formats allocation method for display.
 * Uses lodash startCase for consistent capitalization.
 * @param {AllocationMethod} method - The allocation method to format
 * @param {AllocationSource} source - The allocation source (plat or gis)
 * @return {string} Formatted string for display
 * @example
 * formatAllocationMethod('acreage', 'gis') // "Acreage GIS"
 * formatAllocationMethod('lateralLength', 'plat') // "Lateral Length"
 */
export const formatAllocationMethod = (
  method: AllocationMethod,
  source: AllocationSource
): string => {
  const methodName = startCase(method);
  const sourceName = source === 'gis' ? 'GIS' : '';
  return sourceName ? `${methodName} ${sourceName}` : methodName;
};

/**
 * Formats a value based on allocation method with appropriate unit suffix.
 * @param {number | null} value - The numeric value to format
 * @param {AllocationMethod} allocationMethod - The allocation method determining unit suffix (ft or ac)
 * @return {React.ReactNode} Formatted React node with tooltip, or '--' if null
 */
export const formatValue = (
  value: number | null,
  allocationMethod: AllocationMethod
): React.ReactNode => {
  if (value === null) return '--';
  const unit = allocationMethod === 'lateralLength' ? 'ft' : 'ac';
  return formatNumberWithTooltip(value, 2, '0.00', false, unit);
};

/**
 * Formats a factor value as a percentage.
 * @param {number | null} factor - The factor value (0-1) to format as percentage
 * @return {React.ReactNode} Formatted React node with tooltip showing percentage, or '--' if null
 */
export const formatFactor = (factor: number | null): React.ReactNode => {
  if (factor === null) return '--';
  return formatNumberWithTooltip(factor * 100, 2, '0.00', false, '%');
};
